























































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { getModule } from 'vuex-module-decorators'
import userStore from '@/modules/auth/auth.store'
import matchStore from '@/modules/match/match.store'
import integrationStore from '@/modules/integration/integration.store'
import ServerError from '@/utils/server_error'
import {
  CheckInRoleType,
  MatchGameResultEntity,
  MatchGameState
} from '@/api/match'
import { AdvanceToNextGameDialog } from '@/modules/match/components/AdvanceToNextGameDialog'
import { MatchCompleteDialog } from '@/modules/match/components/MatchCompleteDialog'
import { getRealtimeConnection, realtimeEventTarget } from '@/utils/realtime'
import { HubConnection } from '@microsoft/signalr'
import { CancelError, CancellationToken } from '@/utils/cancel'
import MatchAssistanceRequestDialog from '@/modules/match/components/MatchAssistanceRequestDialog'

interface MatchResultUpdateResponse {
  matchGameId: string
  results: MatchGameResultEntity[]
  state: MatchGameState
}

@Component({
  components: {
    AdvanceToNextGameDialog,
    MatchCompleteDialog,
    MatchAssistanceRequestDialog
  }
})
export default class MatchLayout extends Vue {
  userModule = getModule(userStore, this.$store)
  matchModule = getModule(matchStore, this.$store)
  integrationModule = getModule(integrationStore, this.$store)
  realtimeConnection!: HubConnection
  realtimeConnectionCancellationToken = new CancellationToken()
  isBackgroundError: boolean = false

  @Prop({ required: true }) readonly matchGameId!: string

  loading: boolean = false

  isSupportRequestOpen = false

  @Watch('matchGameId', { immediate: true })
  async onMatchGameIdChanged(matchGameId: string, oldMatchGameId: string) {
    if (!this.realtimeConnection) {
      return
    }

    const oldMatchId = this.matchGame ? this.matchGame!.matchId : null
    await this.fetchData()

    await this.realtimeConnection.send('UnsubscribeMatch', {
      matchGameId: oldMatchGameId,
      matchId: oldMatchId
    })
    await this.realtimeConnection.send('SubscribeMatch', {
      matchGameId,
      matchId: this.matchGame ? this.matchGame!.matchId : null
    })
  }

  @Watch('hasLoggedIn')
  async onHasLoggedInChanged() {
    await this.fetchData()

    try {
      this.realtimeConnection = await getRealtimeConnection(
        this.realtimeConnectionCancellationToken
      )
    } catch (err) {
      if (err instanceof CancelError) {
        return
      }

      this.$toast.error(this.$t('MatchLayout.FailConnectServerMessage'))
      console.error(err)
    }
  }

  async created() {
    await this.fetchData()

    try {
      this.realtimeConnection = await getRealtimeConnection(
        this.realtimeConnectionCancellationToken
      )
    } catch (err) {
      if (err instanceof CancelError) {
        return
      }

      this.$toast.error(this.$t('MatchLayout.FailConnectServerMessage'))
      console.error(err)
    }

    this.realtimeConnection.on('matchResultUpdate', this.onMatchResultUpdate)
    this.realtimeConnection.on('matchComplete', this.onMatchComplete)
    this.realtimeConnection.on('matchRemoved', this.onMatchRemoved)
    this.realtimeConnection.on('advanceToNextGame', this.onAdvanceToNextGame)
    this.realtimeConnection.on(
      'matchScheduleUpdated',
      this.onMatchScheduleUpdated
    )
    await this.realtimeConnection.send('SubscribeMatch', {
      matchGameId: this.matchGameId,
      matchId: this.matchGame ? this.matchGame!.matchId : null
    })

    realtimeEventTarget.addEventListener(
      'reconnected',
      this.onRealtimeReconnected
    )
  }

  async beforeDestroy() {
    await this.matchModule.resetMatchData()
    await this.integrationModule.resetIntegration()

    this.realtimeConnectionCancellationToken.cancel()

    if (this.realtimeConnection) {
      await this.realtimeConnection.send('UnsubscribeMatch', {
        matchGameId: this.matchGameId,
        matchId: this.matchModule.matchGame
          ? this.matchModule.matchGame!.matchId
          : null
      })

      this.realtimeConnection.off('matchResultUpdate', this.onMatchResultUpdate)
      this.realtimeConnection.off('matchComplete', this.onMatchComplete)
      this.realtimeConnection.off('advanceToNextGame', this.onAdvanceToNextGame)
      this.realtimeConnection.off('matchRemoved', this.onMatchRemoved)
      this.realtimeConnection.off(
        'matchScheduleUpdated',
        this.onMatchScheduleUpdated
      )
    }

    realtimeEventTarget.removeEventListener(
      'reconnected',
      this.onRealtimeReconnected
    )
  }

  async onMatchResultUpdate(data: MatchResultUpdateResponse) {
    if (data.matchGameId !== this.matchGameId) {
      return
    }

    const results = data.results.filter(
      result => !result.createdBy || result.createdBy === this.matchTeamId
    )

    if (results.length === 0) {
      await this.fetchMatchGame()
      return
    }

    await this.matchModule.updateMatchGameResults(results)
    this.matchGame!.state = data.state
  }

  async onMatchScheduleUpdated() {
    await this.fetchMatchGame()
    // this.$toast.success('The schedule has been updated')
    this.$toast(
      `<div class="headline font-weight-bold">
        ${this.$t('MatchLayout.MatchScheduleUpdated')}
      </div>`,
      {
        color: 'cyan darken-2',
        classes: ['text-center'],
        dismissable: true,
        queueable: false,
        vertical: true,
        timeout: 10 * 1000
      }
    )
  }

  async fetchMatch() {
    try {
      await this.matchModule.fetchMatch(this.matchModule.matchGame!.matchId)
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error(this.$t('MatchLayout.SystemErrorMessage'))
        console.error(err)
      }
      throw err
    }
  }

  async fetchMatchGame() {
    try {
      await this.matchModule.fetchMatchGame(this.matchGameId, true)
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error(this.$t('MatchLayout.SystemErrorMessage'))
        console.error(err)
      }
      throw err
    }
  }

  async fetchMatchGameResults() {
    try {
      await this.matchModule.fetchMatchGameResults(this.matchGameId)
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error(this.$t('MatchLayout.SystemErrorMessage'))
        console.error(err)
      }
      throw err
    }
  }

  async fetchTournament() {
    try {
      await this.matchModule.fetchTournament()
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error(this.$t('MatchLayout.SystemErrorMessage'))
        console.error(err)
      }
      throw err
    }
  }

  async fetchStage() {
    try {
      await this.matchModule.fetchStage()
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error(this.$t('MatchLayout.SystemErrorMessage'))
        console.error(err)
      }
      throw err
    }
  }

  async fetchData() {
    try {
      this.loading = true

      await this.fetchMatchGame()
      await this.fetchMatchGameResults()
      await this.fetchMatch()
      await this.fetchTournament()
      await this.fetchStage()
    } catch {
      await this.$router.push('/')
    } finally {
      this.loading = false
    }
  }

  async onRealtimeReconnected() {
    // refetch data from api
    await this.fetchData()

    // ensure we have subscribed the match
    await this.realtimeConnection.send('SubscribeMatch', {
      matchGameId: this.matchGameId,
      matchId: this.matchGame ? this.matchGame!.matchId : null
    })
  }

  async onMatchComplete() {
    await this.fetchData()
  }

  async onMatchRemoved() {
    await this.$router.push('/')
    this.$toast.error(this.$t('MatchLayout.MatchRemoved'), {
      timeout: 0,
      showClose: true
    })
  }

  async onAdvanceToNextGame() {
    await this.fetchData()
    await this.matchModule.setLeagueLobby(null)
    await this.integrationModule.setJoiningCustomGameFailed(false)
  }

  get match() {
    return this.matchModule.match
  }

  get matchGame() {
    return this.matchModule.matchGame
  }

  get tournament() {
    return this.matchModule.tournament
  }

  get isLoading(): boolean {
    return !this.match || !this.matchGame || this.loading
  }

  get userId() {
    return this.userModule.userId
  }

  get hasLoggedIn() {
    return this.userModule.hasLoggedIn
  }

  get matchTeamId() {
    const checkIn = this.matchGame!.checkIns.find(
      checkIn => checkIn.member.memberId === this.userId
    )

    if (!checkIn) {
      return ''
    }

    return checkIn.matchTeamId
  }

  get matchBackgroundStyles() {
    if (this.isBackgroundError) {
      return {
        backgroundImage: `url(${require('@/assets/default/tournament_background.png')})`
      }
    }
    return {
      backgroundImage: `url(${
        this.tournament !== null ? this.tournament.detailBanner : ''
      })`
    }
  }

  @Watch('integrationConnected')
  async onIntegrationConnectionStatusChanged(status: boolean) {
    if (status) {
      this.$toast.success(
        this.$t('MatchLayout.IntegrationWelcomeUser', {
          name: this.sessionInfo!.name
        }) +
          `<br/>` +
          this.$t('MatchLayout.IntegrationConnectedLOLMessage')
      )
    }
  }

  get integrationConnected() {
    return this.integrationModule.connectionStatus
  }

  get sessionInfo() {
    if (!this.match) return null
    console.log(this.match)

    return this.integrationModule.userInfo
  }

  get mobileFooterVisible() {
    return this.matchModule.mobileFooterVisible
  }
}
